import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TodoSourceProjectsResponse } from "@portal/schemas";
import { Checkbox, Dialog, DialogContent, DialogHeader } from "@portal/ui";
import { fetchBacklogProjects, updateBacklogExcludedProjects } from "../../api/client";
import { TODO_BOARD_KEY } from "../../api/queries";
import { DialogTitleWithHint } from "../../components/Hint";
import { InlineError } from "../../components/InlineError";

export const BACKLOG_PROJECTS_KEY = ["todos", "backlog-projects"] as const;

/**
 * Backlog から取り込むプロジェクトを選ぶ(本人ごと。2026-10-14)。たとえば、工数管理から自動で課題が
 * 作られる個人のプロジェクトを外せる。外すとすぐ保存し、そのプロジェクトのカードは列やメモに関係なく
 * ボードから消える(ユーザー決定)。戻すと、すぐ取り込み直してまた入る。
 */
export function BacklogProjectsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: BACKLOG_PROJECTS_KEY,
    queryFn: fetchBacklogProjects,
    enabled: open,
  });
  const projects = list.data?.status === "connected" ? list.data.projects : [];
  const save = useMutation({
    mutationFn: (excluded: string[]) => updateBacklogExcludedProjects(excluded),
    onMutate: (excluded) => {
      // 先にチェックを切り替えて見せる(保存の結果でボードを置き換える)
      queryClient.setQueryData<TodoSourceProjectsResponse>(BACKLOG_PROJECTS_KEY, (d) =>
        d?.status === "connected"
          ? {
              ...d,
              projects: d.projects.map((p) => ({ ...p, excluded: excluded.includes(p.key) })),
            }
          : d,
      );
    },
    onSuccess: (board) => queryClient.setQueryData(TODO_BOARD_KEY, board),
    onError: () => void queryClient.invalidateQueries({ queryKey: BACKLOG_PROJECTS_KEY }),
  });
  const toggle = (key: string, include: boolean) => {
    const excluded = projects.filter((p) => p.excluded).map((p) => p.key);
    save.mutate(include ? excluded.filter((k) => k !== key) : [...excluded, key]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* プロジェクトが多くても見渡せるよう、画面を広く使う(ユーザー要望 2026-10-14)。一覧は中でスクロールする */}
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-5xl">
        <DialogHeader>
          <DialogTitleWithHint hint="チェックを外したプロジェクトの課題は、ToDo に取り込みません。戻すと、また取り込みます。">
            取り込むプロジェクト
          </DialogTitleWithHint>
        </DialogHeader>
        {/* 消えて戻らないものがあるので、注意は画面に出す */}
        <p className="text-xs text-destructive">
          外すと、そのプロジェクトのカードはメモも含めて消えます。
        </p>
        {list.isPending && <p className="text-sm text-muted-foreground">読み込み中…</p>}
        {(list.isError || list.data?.status === "disconnected") && (
          <p role="alert" className="text-sm text-destructive">
            Backlog のプロジェクトを取得できませんでした。
          </p>
        )}
        {projects.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {projects.length} 件中 {projects.filter((p) => !p.excluded).length} 件を取り込み中
          </p>
        )}
        {projects.length > 0 && (
          <ul
            aria-label="Backlog のプロジェクト"
            className="-mx-2 grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-x-4 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3"
          >
            {projects.map((p) => (
              <li key={p.key}>
                <label
                  title={`${p.name}(${p.key})`}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <Checkbox
                    checked={!p.excluded}
                    disabled={save.isPending}
                    onCheckedChange={(v) => toggle(p.key, v === true)}
                    aria-label={p.name}
                  />
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{p.key}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <InlineError error={save.error} />
      </DialogContent>
    </Dialog>
  );
}
